import { AsText, AsNumber, Spacer, AsButton } from '@adserve/adserve-react-components';
import React, { useContext, useEffect, useState } from 'react';
import { cellPadding, inputStyle, labelStyle, valuePotPanelStyle } from './CampaignBriefStyles';
import { SystemSettingsContext } from '../../../contexts/SystemSettingsContext';
import { ValidateJcn } from '../../../services/CampaignsDataService';
import { BusinessTypes } from '../../../constants/Enums';
import { AirtimePlanningContext } from '../../../contexts/AirtimePlanningContext';
import { CampaignGoalEnum, DealTypes } from '../../../constants/Enums';

const errorStyle = {
    borderColor: '#C40606',
    backgroundColor: '#FFF3F3'
};

const statusLabelStyle = {
    position: 'absolute',
    color: '#C40606',
    fontSize: 12
};

const ValuePot = ({
    campaign,
    valuePotAvailable,
    updateCampaign,
    readOnly = false,
    activeRequest = {},
    onValuePotRequestApprovalClick,
    valuePotApprovalWorkflowId,
    setValidValuePotJcn,
    validValuePotJcn,
    isSAndP,
    selectedGoal,
    vatDates = [],
    currentDeal
}) => {
    const { formatCurrency, formatNumber } = useContext(SystemSettingsContext);
    const { propertyStats } = useContext(AirtimePlanningContext);
    const [billableAmount, setBillableAmount] = useState(0);
    const handleRequestApprovalClick = () => {
        if (onValuePotRequestApprovalClick) {
            onValuePotRequestApprovalClick();
        }
    };

    const calcVatRate = (vatRateId) => {
        if (!vatDates?.length) {
            return 1;
        }
        const vatDate = vatDates.find((vd) => vd.vatId === vatRateId);
        if (!vatDate) {
            return 1;
        }
        const rate = vatDate.rate ? vatDate.rate : 1;
        return rate;
    };

    const calcOffairTotals = () => {
        let invoiceTotal = 0;
        let commissionTotal = 0;
        campaign?.offairActivities?.forEach((oa) => {
            if (oa.status !== 5) {
                const amount = parseFloat(oa.price);
                let commission = 0;
                if (oa.commission > 0) {
                    commission = (amount * parseFloat(oa.commission)) / 100;
                }
                invoiceTotal += amount;
                commissionTotal += commission;
            }
        });
        return {
            invoiceTotal,
            commissionTotal
        };
    };

    const sumAllPropertiesLines = (field) => {
        let total = 0;
        if (propertyStats?.length) {
            propertyStats?.forEach((b) => {
                if (b !== undefined) {
                    total = total + (b[field] !== undefined ? parseFloat(b[field] ?? 0) : 0);
                }
            });
        }
        return total?.toFixed(2);
    };

    let campaignTotal = parseFloat(campaign?.budget ?? 0);
    if (isSAndP) {
        campaignTotal += parseFloat(sumAllPropertiesLines('sandPBudget'));
    }

    const offairTotal = calcOffairTotals();

    campaignTotal -=
        isSAndP || (currentDeal?.type === DealTypes.AGENCY && !!currentDeal?.maxValuePot) ? campaign?.valuePotSpend : 0;

    const commissionRate = !campaign.barterAgencyAccountId ? campaign.commissionRate : campaign.barterCommissionRate;
    const campaignCommission = (campaignTotal * commissionRate) / 100 + offairTotal?.commissionTotal;
    const campaignTotalMinusCommission = campaignTotal + offairTotal?.invoiceTotal - campaignCommission;
    const vatTotal = (calcVatRate(campaign.vatId) * campaignTotalMinusCommission) / 100;
    const billingAmount = campaignTotalMinusCommission + vatTotal;

    useEffect(() => {
        if (campaign.valuePotSpend) {
            const value = campaign.valuePotSpend;
            if (value[value?.length - 1] === '.') {
                return;
            }
            const spend = parseFloat(((campaign?.budget * campaign?.valuePotSpendPct) / 100)?.toFixed(2));

            if (campaign.valuePotSpend !== spend) {
                updateCampaign('valuePotSpend', spend);
            }

            if (isSAndP) {
                setBillableAmount(billingAmount);
            } else {
                setBillableAmount(campaign.budget - campaign?.valuePotSpend);
            }
        }
    }, [campaign.budget, campaign?.valuePotSpend]);

    const checkLastCharIsDp = (value) => {
        return value[value?.length - 1] !== '.';
    };
    const handleApplyvaluePotSpendChange = (event) => {
        let value = event.target.value;
        if (value < 0) {
            value = 0;
        }

        const budget = parseFloat(campaign?.budget);
        if (checkLastCharIsDp(value)) {
            value = value === '' ? 0 : parseFloat(value);
            if (value > budget) {
                value = budget;
            }
        }

        if (budget > 0 && value !== 0) {
            updateCampaign('valuePotSpend', value);
            if (checkLastCharIsDp(value)) {
                updateCampaign('valuePotSpendPct', (value * 100) / budget);
            }
        }

        if (value === 0) {
            updateCampaign('valuePotSpendPct', 0);
            updateCampaign('valuePotSpend', 0);
        }
    };

    const handleApplyValuePotPctChange = (event) => {
        let value = event.target.value;
        if (value < 0) {
            value = 0;
        }
        if (value > 100) {
            value = 100;
        }

        if (campaign?.budget > 0 && checkLastCharIsDp(value)) {
            updateCampaign('valuePotSpendPct', value);
            updateCampaign('valuePotSpend', (campaign?.budget * value) / 100);
        } else {
            updateCampaign('valuePotSpendPct', value);
        }
    };

    const handleApplyJCNChange = (event) => {
        updateCampaign('valuePotJCN', event.target.value);
    };

    const handleValidateJCN = async (event) => {
        const result = await ValidateJcn(event.target.value);
        setValidValuePotJcn(result);
    };

    return (
        <>
            {campaign?.businessType?.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase() ? (
                <>
                    <div style={{ ...valuePotPanelStyle, padding: '11px 55px 17px 11px' }}>
                        <span style={{ fontSize: 15, fontWeight: 500 }}>{window.translate('Value Pot')}</span>
                        <div style={{ display: 'flex', paddingTop: 8 }}>
                            <div>
                                <AsNumber
                                    id='valuePot-percentage'
                                    title={window.translate('% to apply')}
                                    labelStyle={labelStyle}
                                    inputStyle={{ ...inputStyle, width: 70 }}
                                    value={campaign?.valuePotSpendPct || 0}
                                    onChange={handleApplyValuePotPctChange}
                                    disabled={readOnly}
                                    allowFloat={true}
                                    formatCurrency={formatNumber}
                                    formatNumberOptions={{ maximumFractionDigits: 10 }}
                                    digitLimit={10}
                                />
                            </div>
                            <Spacer width={15.5} />
                            <div>
                                <AsNumber
                                    id='valuePot-apply-amount'
                                    title={window.translate('Value Amount')}
                                    labelStyle={labelStyle}
                                    inputStyle={{ ...inputStyle, width: 136 }}
                                    value={campaign?.valuePotSpend}
                                    onChange={handleApplyvaluePotSpendChange}
                                    disabled={readOnly}
                                    allowFloat={true}
                                    formatCurrency={formatCurrency}
                                    digitLimit={10}
                                />
                            </div>
                            <Spacer width={15.5} />
                            <div>
                                <AsNumber
                                    id='valuePot-billable-amount'
                                    title={window.translate('Net Billable amount')}
                                    labelStyle={labelStyle}
                                    inputStyle={{ ...inputStyle, width: 136 }}
                                    value={campaignTotalMinusCommission}
                                    disabled={true}
                                    allowFloat={true}
                                    formatCurrency={formatCurrency}
                                    digitLimit={10}
                                />
                            </div>
                            <Spacer width={15.5} />
                            {/* <div style={{ width: 205 }}>
                    {(campaign?.valuePotSpend === '' || Number.parseFloat(campaign?.valuePotSpend) <= Number.parseFloat(campaign?.budget)) && (
                        <AsText
                            id='valuePot-jcn'
                            title={window.translate('Additional JCN Number')}
                            labelStyle={labelStyle}
                            inputStyle={{
                                ...inputStyle,
                                width: 205,
                                ...(!validValuePotJcn ? errorStyle : {})
                            }}
                            value={campaign?.valuePotJCN}
                            onChange={handleApplyJCNChange}
                            disabled={readOnly}
                            onBlur={handleValidateJCN}
                        />
                    )}
                    {!validValuePotJcn && (
                        <label style={statusLabelStyle}>{window.translate('Invalid JCN - check digit failed')}</label>
                    )}
                </div> */}
                            <Spacer width={51} />
                            <div
                                style={{
                                    display: valuePotApprovalWorkflowId ? 'flex' : 'none',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    fontSize: 15,
                                    fontWeight: 'bold',
                                    boxSizing: 'border-box',
                                    height: 124,
                                    width: 269,
                                    border: '1px solid #D7D7D7',
                                    borderRadius: 8,
                                    marginLeft: 30,
                                    marginTop: -23
                                }}
                            >
                                <div style={{ marginTop: 11 }}>
                                    <span>{`${window.translate('Approval Status')}: ${
                                        activeRequest?.state?.name || window.translate('Not requested')
                                    }`}</span>
                                </div>
                                <AsButton
                                    style={{
                                        fontSize: 15,
                                        fontWeight: 'bold',
                                        marginTop: 14
                                    }}
                                    handleClick={handleRequestApprovalClick}
                                    disabled={activeRequest?.id || !campaign.valuePotSpend}
                                >
                                    {window.translate('Request Approval')}
                                </AsButton>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div style={{ ...valuePotPanelStyle, padding: '11px 10px 17px 188px' }}>
                        <span style={{ fontSize: 15, fontWeight: 500, paddingLeft: 42 }}>
                            {window.translate('Value Pot')}
                        </span>
                        <div style={{ display: 'flex', width: '100%' }}>
                            <div style={{ width: '20%', padding: cellPadding }}>
                                <div style={{ display: 'flex' }}>
                                    <div>
                                        <AsNumber
                                            id='valuePot-percentage'
                                            title={window.translate('% to apply')}
                                            labelStyle={labelStyle}
                                            inputStyle={{ ...inputStyle, width: '72%' }}
                                            value={campaign?.valuePotSpendPct || 0}
                                            onChange={handleApplyValuePotPctChange}
                                            disabled={readOnly}
                                            allowFloat={true}
                                            formatCurrency={formatNumber}
                                            formatNumberOptions={{ maximumFractionDigits: 10 }}
                                            digitLimit={10}
                                        />
                                    </div>
                                    <div style={{ marginLeft: -20 }}>
                                        <AsNumber
                                            id='valuePot-apply-amount'
                                            title={window.translate('Value Amount')}
                                            labelStyle={labelStyle}
                                            inputStyle={{ ...inputStyle, width: '102%' }}
                                            value={campaign?.valuePotSpend}
                                            onChange={handleApplyvaluePotSpendChange}
                                            disabled={readOnly}
                                            allowFloat={true}
                                            formatCurrency={formatCurrency}
                                            digitLimit={10}
                                        />
                                    </div>
                                    <Spacer height={0} width={9} />
                                    <div>
                                        <AsNumber
                                            id='valuePot-billable-amount'
                                            title={window.translate('Net Billable amount')}
                                            labelStyle={{ ...labelStyle, width: '154%' }}
                                            inputStyle={{ ...inputStyle, width: '106%' }}
                                            value={campaignTotalMinusCommission}
                                            disabled={true}
                                            allowFloat={true}
                                            formatCurrency={formatCurrency}
                                            digitLimit={10}
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* <div style={{ width: 205 }}>
                    {(campaign?.valuePotSpend === '' || Number.parseFloat(campaign?.valuePotSpend) <= Number.parseFloat(campaign?.budget)) && (
                        <AsText
                            id='valuePot-jcn'
                            title={window.translate('Additional JCN Number')}
                            labelStyle={labelStyle}
                            inputStyle={{
                                ...inputStyle,
                                width: 205,
                                ...(!validValuePotJcn ? errorStyle : {})
                            }}
                            value={campaign?.valuePotJCN}
                            onChange={handleApplyJCNChange}
                            disabled={readOnly}
                            onBlur={handleValidateJCN}
                        />
                    )}
                    {!validValuePotJcn && (
                        <label style={statusLabelStyle}>{window.translate('Invalid JCN - check digit failed')}</label>
                    )}
                </div> */}
                            <div style={{ width: '25%', padding: cellPadding }}>
                                <div
                                    style={{
                                        display: valuePotApprovalWorkflowId ? 'flex' : 'none',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        fontSize: 15,
                                        fontWeight: 'bold',
                                        boxSizing: 'border-box',
                                        height: 124,
                                        width: 269,
                                        border: '1px solid #D7D7D7',
                                        borderRadius: 8,
                                        marginTop: -23
                                    }}
                                >
                                    <div style={{ marginTop: 11 }}>
                                        <span>{`${window.translate('Approval Status')}: ${
                                            activeRequest?.state?.name || window.translate('Not requested')
                                        }`}</span>
                                    </div>
                                    <AsButton
                                        style={{
                                            fontSize: 15,
                                            fontWeight: 'bold',
                                            marginTop: 14
                                        }}
                                        handleClick={handleRequestApprovalClick}
                                        disabled={activeRequest?.id || !campaign.valuePotSpend}
                                    >
                                        {window.translate('Request Approval')}
                                    </AsButton>
                                </div>
                            </div>
                            <div style={{ width: '25%', padding: cellPadding }}></div>
                            <div style={{ width: '25%', padding: cellPadding }}></div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default ValuePot;
